import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ParamsProvider, useTabManager } from './tab-manager';

export const useTabViewManager = () => {
  const tabViewsRef = useRef<Record<string, any>>({});
  const subscribersRef = useRef<Set<(views: Record<string, any>) => void>>(
    new Set()
  );

  const updateTabViews = useCallback((tabId: string, view: any) => {
    tabViewsRef.current = {
      ...tabViewsRef.current,
      [tabId]: view,
    };
    // Notify all subscribers
    subscribersRef.current.forEach((callback) => callback(tabViewsRef.current));
  }, []);

  const subscribeToTabViews = useCallback(
    (callback: (views: Record<string, any>) => void) => {
      subscribersRef.current.add(callback);
      return () => subscribersRef.current.delete(callback);
    },
    []
  );

  return {
    registerTabView: (tabId: string, view: ReactNode) => {
      if (tabViewsRef.current[tabId] === undefined) {
        updateTabViews(tabId, view);
      }
    },
    unregisterTabView: (tabId: string) => {
      if (tabViewsRef.current[tabId] === undefined) {
        return;
      }
      tabViewsRef.current = Object.fromEntries(
        Object.entries(tabViewsRef.current).filter(([id]) => id !== tabId)
      );
      subscribersRef.current.forEach((callback) =>
        callback(tabViewsRef.current)
      );
    },
    views: tabViewsRef,
    subscribe: subscribeToTabViews,
  };
};

export const useTabViewsState = (): [Record<string, ReactNode>, number] => {
  const { tabViewManager } = useTabManager();
  const [tabs, setTabs] = useState<Record<string, ReactNode>>(
    tabViewManager.views.current ?? {}
  );
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const unsubscribe = tabViewManager.subscribe((views) => {
      setTabs(views);
      setVersion(version + 1);
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [tabs, version];
};

//used to "register" a tab view
export const Tab: React.FC<{
  children: ReactNode;
  tabId: string;
  params?: Record<string, string | number>;
}> = ({ children, tabId, params }) => {
  const { tabViewManager, renderedTabs } = useTabManager();

  useEffect(() => {
    const tabView = renderedTabs[tabId]!;
    tabViewManager.registerTabView(
      tabId,
      <ParamsProvider
        tabId={tabId}
        params={{
          ...tabView.params,
          ...params,
          tabId,
          order: tabView.order,
          route: tabView.route,
        }}
      >
        {children}
      </ParamsProvider>
    );

    return () => {
      tabViewManager.unregisterTabView(tabId);
    };
  }, [tabViewManager, tabId, children, renderedTabs, params]);

  return null;
};
